/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import { AIReview } from "../../../foundation/api/model";

import AIReviewScreenStatus from "../../../foundation/api/model/AIReview/AIReviewScreenStatus";
import AIReviewStatus from "../../../foundation/api/model/AIReview/AIReviewStatus";
import uniquifyArrayOfObjects from "../../../foundation/utils/uniquify";
import { ApprovalStatus } from "../../containers/AppContainer/ApprovalsContainer/ApprovalsStatus";

import AppActionTypes from "../app/AppActionTypes";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import { AllPayloads } from "../payloads";

import * as Payloads from "./ApprovalsActionPayloads";
import ApprovalsActionTypes from "./ApprovalsActionTypes";
import { AIReviewsRecord, ApprovalsRecord, ProjectApprovalsRecord, WorkspaceApprovalsRecord } from "./types";

interface State {
    isGetApprovalsPending: boolean;
    isCreateApprovalPending: boolean;
    isUpdateApprovalPending: boolean;
    isUpdateApprovalReviewPending: boolean;
    isReRequestApprovalReviewPending: boolean;
    isDeleteApprovalReviewerPending: boolean;
    isCreateAIReviewPending: boolean;
    projectApprovalsRecord: ProjectApprovalsRecord;
    aiReviewsRecord: AIReviewsRecord;
    workspaceApprovalsRecord: WorkspaceApprovalsRecord;
}

class ApprovalsDataStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            isGetApprovalsPending: true,
            isCreateApprovalPending: false,
            isUpdateApprovalPending: false,
            isUpdateApprovalReviewPending: false,
            isReRequestApprovalReviewPending: false,
            isDeleteApprovalReviewerPending: false,
            isCreateAIReviewPending: false,
            projectApprovalsRecord: {},
            aiReviewsRecord: {},
            workspaceApprovalsRecord: {}
        };
    }

    #getProjectApprovals(state: State, pid: string): ApprovalsRecord | undefined {
        return state.projectApprovalsRecord[pid];
    }

    #getWorkspaceApprovals(state: State, oid: string): ApprovalsRecord | undefined {
        return state.workspaceApprovalsRecord[oid];
    }

    reset(state: State): State {
        return {
            ...this.getInitialState(),
            projectApprovalsRecord: state.projectApprovalsRecord,
            workspaceApprovalsRecord: state.workspaceApprovalsRecord,
            aiReviewsRecord: state.aiReviewsRecord
        };
    }

    getApprovalsSuccess(state: State, {
        pid,
        approvals,
        aiReviews = []
    }: Payloads.GetProjectApprovalsSuccess): State {
        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: Object.fromEntries(approvals.map(approval => [approval._id, approval]))
            },
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: Object.fromEntries(aiReviews?.map(aiReview => [aiReview._id, aiReview]))
            },
            isGetApprovalsPending: false
        };
    }

    getApprovalsFinish(state: State): State {
        return {
            ...state,
            isGetApprovalsPending: false
        };
    }

    getWorkspaceApprovalsSuccess(state: State, {
        oid,
        approvals
    }: Payloads.GetWorkspaceApprovalsSuccess): State {
        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: Object.fromEntries(approvals.map(approval => [approval._id, approval]))
            }
        };
    }

    createApproval(state: State): State {
        return {
            ...state,
            isCreateApprovalPending: true
        };
    }

    createApprovalSuccess(state: State, {
        pid,
        approval
    }: Payloads.CreateApprovalSuccess): State {
        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: {
                    ...(this.#getProjectApprovals(state, pid) || {}),
                    [approval._id]: approval
                }
            },
            isCreateApprovalPending: false
        };
    }

    createApprovalFailure(state: State): State {
        return {
            ...state,
            isCreateApprovalPending: false
        };
    }

    addApproval(state: State, {
        pid,
        approval
    }: Payloads.AddApproval): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);

        if (!projectApprovals) {
            return state;
        }

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: {
                    ...projectApprovals,
                    [approval._id]: approval
                }
            }
        };
    }

    updateApproval(state: State): State {
        return {
            ...state,
            isUpdateApprovalPending: true
        };
    }

    updateApprovalSuccess(state: State, {
        pid,
        apid,
        payload,
        dots
    }: Payloads.UpdateApprovalSuccess): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);
        const approval = projectApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: {
                    ...projectApprovals,
                    [apid]: {
                        ...approval,
                        name: payload.name || approval.name,
                        updatedAt: new Date().toISOString(),
                        note: payload.note || undefined,
                        dots: uniquifyArrayOfObjects([...(approval.dots || []), ...(dots || [])], "did")
                    }
                }
            },
            isUpdateApprovalPending: false
        };
    }

    updateApprovalFailure(state: State): State {
        return {
            ...state,
            isUpdateApprovalPending: false
        };
    }

    updateApprovalReview(state: State): State {
        return {
            ...state,
            isUpdateApprovalReviewPending: true
        };
    }

    updateApprovalReviewSuccess(state: State, {
        pid,
        apid,
        reviewerId,
        payload,
        dots
    }: Payloads.UpdateApprovalReviewSuccess): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);
        const approval = projectApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: {
                    ...projectApprovals,
                    [apid]: {
                        ...approval,
                        dots: uniquifyArrayOfObjects([...(approval.dots || []), ...(dots || [])], "did"),
                        reviews: approval.reviews.map(review => {
                            if (review.reviewedBy?._id === reviewerId) {
                                return {
                                    ...review,
                                    ...payload,
                                    reviewedAt: new Date().toISOString()
                                };
                            }

                            return review;
                        })
                    }
                }
            },
            isUpdateApprovalReviewPending: false
        };
    }

    updateApprovalReviewFailure(state: State): State {
        return {
            ...state,
            isUpdateApprovalReviewPending: false
        };
    }

    reRequestApprovalReview(state: State): State {
        return {
            ...state,
            isReRequestApprovalReviewPending: true
        };
    }

    reRequestApprovalReviewSuccess(state: State, {
        pid,
        apid,
        aprid
    }: Payloads.ReRequestApprovalReviewSuccess): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);
        const approval = projectApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: {
                    ...projectApprovals,
                    [apid]: {
                        ...approval,
                        reviews: approval.reviews.map(review => {
                            if (review.reviewedBy?._id === aprid) {
                                return {
                                    ...review,
                                    status: ApprovalStatus.PENDING
                                };
                            }

                            return review;
                        })
                    }
                }
            },
            isReRequestApprovalReviewPending: false
        };
    }

    reRequestApprovalReviewFailure(state: State): State {
        return {
            ...state,
            isReRequestApprovalReviewPending: false
        };
    }

    deleteApprovalReviewer(state: State): State {
        return {
            ...state,
            isDeleteApprovalReviewerPending: true
        };
    }

    deleteApprovalReviewerSuccess(state: State, {
        pid,
        apid,
        aprid
    }: Payloads.DeleteApprovalReviewerSuccess): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);
        const approval = projectApprovals?.[apid];

        if (!approval) {
            return state;
        }

        const filteredReviews = approval.reviews.filter(review => review.reviewedBy?._id !== aprid);
        const updatedProjectApprovals = { ...projectApprovals };

        if (filteredReviews.length) {
            updatedProjectApprovals[apid] = {
                ...approval,
                reviews: filteredReviews
            };
        } else {
            delete updatedProjectApprovals[apid];
        }

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: updatedProjectApprovals
            },
            isDeleteApprovalReviewerPending: false
        };
    }

    deleteApprovalReviewerFailure(state: State): State {
        return {
            ...state,
            isDeleteApprovalReviewerPending: false
        };
    }

    deleteApproval(state: State, {
        pid,
        apid
    }: Payloads.DeleteApproval): State {
        const projectApprovals = this.#getProjectApprovals(state, pid);

        if (!projectApprovals) {
            return state;
        }

        const updatedProjectApprovals = { ...projectApprovals };

        delete updatedProjectApprovals[apid];

        return {
            ...state,
            projectApprovalsRecord: {
                ...state.projectApprovalsRecord,
                [pid]: updatedProjectApprovals
            }
        };
    }

    addWorkspaceApproval(state: State, {
        oid,
        approval
    }: Payloads.AddWorkspaceApproval): State {
        const workspaceApprovals = this.#getWorkspaceApprovals(state, oid);

        if (!workspaceApprovals) {
            return state;
        }

        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: {
                    ...workspaceApprovals,
                    [approval._id]: approval
                }
            }
        };
    }

    updateWorkspaceApproval(state: State, {
        oid,
        apid,
        payload
    }: Payloads.UpdateWorkspaceApproval): State {
        const workspaceApprovals = this.#getWorkspaceApprovals(state, oid);
        const approval = workspaceApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: {
                    ...workspaceApprovals,
                    [apid]: {
                        ...approval,
                        name: payload.name || approval.name,
                        updatedAt: new Date().toISOString(),
                        note: payload.note || undefined
                    }
                }
            }
        };
    }

    updateWorkspaceApprovalReview(state: State, {
        oid,
        apid,
        reviewerId,
        payload
    }: Payloads.UpdateWorkspaceApprovalReview): State {
        const workspaceApprovals = this.#getWorkspaceApprovals(state, oid);
        const approval = workspaceApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: {
                    ...workspaceApprovals,
                    [apid]: {
                        ...approval,
                        reviews: approval.reviews.map(review => {
                            if (review.reviewedBy?._id === reviewerId) {
                                return {
                                    ...review,
                                    ...payload,
                                    reviewedAt: new Date().toISOString()
                                };
                            }

                            return review;
                        })
                    }
                }
            }
        };
    }

    deleteWorkspaceApprovalReviewer(state: State, {
        oid,
        apid,
        aprid
    }: Payloads.DeleteWorkspaceApprovalReviewer): State {
        const workspaceApprovals = this.#getWorkspaceApprovals(state, oid);
        const approval = workspaceApprovals?.[apid];

        if (!approval) {
            return state;
        }

        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: {
                    ...workspaceApprovals,
                    [apid]: {
                        ...approval,
                        reviews: approval.reviews.filter(review => review.reviewedBy?._id !== aprid)
                    }
                }
            }
        };
    }

    deleteWorkspaceApproval(state: State, {
        oid,
        apid
    }: Payloads.DeleteWorkspaceApproval): State {
        const workspaceApprovals = this.#getWorkspaceApprovals(state, oid);

        if (!workspaceApprovals) {
            return state;
        }

        const updatedWorkspaceApprovals = { ...workspaceApprovals };

        delete updatedWorkspaceApprovals[apid];

        return {
            ...state,
            workspaceApprovalsRecord: {
                ...state.workspaceApprovalsRecord,
                [oid]: updatedWorkspaceApprovals
            }
        };
    }

    createAIReview(state: State): State {
        return {
            ...state,
            isCreateAIReviewPending: true
        };
    }

    createAIReviewSuccess(state: State, {
        pid,
        aiReview
    }: Payloads.CreateAIReviewSuccess): State {
        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...state.aiReviewsRecord[pid],
                    [aiReview._id]: aiReview
                }
            },
            isCreateAIReviewPending: false
        };
    }

    createAIReviewFailure(state: State): State {
        return {
            ...state,
            isCreateAIReviewPending: false
        };
    }

    updateAIReview(state: State, {
        pid,
        airid,
        update
    }: Payloads.UpdateAIReview): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedScreenMap = Object.fromEntries(
            update.screens.map(updatedScreen => [updatedScreen.id, updatedScreen])
        );

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: {
                        ...aiReviews[airid],
                        status: update.status,
                        screens: aiReviews[airid].screens.map(existingScreen =>
                            updatedScreenMap[existingScreen.id] ?? existingScreen
                        )
                    }
                }
            }
        };
    }

    createAIReviewFeedback(state: State, {
        pid,
        airid,
        feedback
    }: Payloads.CreateAIReviewFeedback): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            feedback
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    deleteAIReviewFeedback(state: State, {
        pid,
        airid
    }: Payloads.DeleteAIReviewFeedback): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview = { ...aiReviews[airid] };
        delete updatedReview.feedback;

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    createAiReviewScreenDotRating(state: State, {
        pid,
        airid,
        sid,
        did,
        rating
    }: Payloads.CreateAIReviewScreenDotRating): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        dots: screen.dots?.map(dot => {
                            if (dot._id === did) {
                                return {
                                    ...dot,
                                    rating
                                };
                            }

                            return dot;
                        })
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    updateAIReviewStatus(
        state: State,
        { pid, airid, status }: Payloads.UpdateAIReviewStatus
    ): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            status
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    updateAIReviewScreenStatus(
        state: State,
        { pid, airid, sid, reviewStatus }: Payloads.UpdateAIReviewScreenStatus
    ): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        reviewStatus
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    updateAIReviewScreenCategoryStatus(state: State, {
        pid,
        airid,
        sid,
        category,
        status
    }: Payloads.UpdateAIReviewScreenCategoryStatus): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        categoryStatuses: {
                            ...screen.categoryStatuses,
                            [category]: status
                        }
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    createAIReviewScreenDot(state: State, {
        pid,
        airid,
        sid,
        aiReviewDot
    }: Payloads.CreateAIReviewScreenDot): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        dots: [...(screen.dots ?? []), aiReviewDot]
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    updateAIReviewScreenDot(state: State, {
        pid,
        airid,
        sid,
        did,
        dotData
    }: Payloads.UpdateAIReviewScreenDot): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        dots: screen.dots?.map(dot => {
                            if (dot._id === did) {
                                return {
                                    ...dot,
                                    ...dotData
                                };
                            }

                            return dot;
                        })
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    updateAIReviewScreenDotStatus(state: State, {
        pid,
        airid,
        sid,
        did,
        status
    }: Payloads.UpdateAIReviewScreenDotStatus): State {
        const aiReviews = state.aiReviewsRecord[pid];

        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        dots: screen.dots?.map(dot => {
                            if (dot._id === did) {
                                return {
                                    ...dot,
                                    status
                                };
                            }

                            return dot;
                        })
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    deleteAIReviewScreenDot(
        state: State,
        { pid, airid, sid, did }: Payloads.DeleteAIReviewScreenDot
    ): State {
        const aiReviews = state.aiReviewsRecord[pid];
        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const updatedReview: AIReview = {
            ...aiReviews[airid],
            screens: aiReviews[airid].screens.map(screen => {
                if (screen.id === sid) {
                    return {
                        ...screen,
                        dots: screen.dots?.filter(dot => dot._id !== did)
                    };
                }

                return screen;
            })
        };

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: updatedReview
                }
            }
        };
    }

    deleteAIReview(
        state: State,
        { pid, airid }: Payloads.DeleteAIReview
    ): State {
        const projectAIReviews = state.aiReviewsRecord[pid];
        if (!projectAIReviews || !projectAIReviews[airid]) {
            return state;
        }

        const updatedProjectAIReviews = { ...projectAIReviews };

        delete updatedProjectAIReviews[airid];

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: updatedProjectAIReviews
            }
        };
    }

    stopAIReview(
        state: State,
        { pid, airid }: Payloads.StopAIReview
    ): State {
        const aiReviews = state.aiReviewsRecord[pid];

        if (!aiReviews || !aiReviews[airid]) {
            return state;
        }

        const currentReview = aiReviews[airid];

        return {
            ...state,
            aiReviewsRecord: {
                ...state.aiReviewsRecord,
                [pid]: {
                    ...aiReviews,
                    [airid]: {
                        ...currentReview,
                        status: AIReviewStatus.CANCELLED,
                        screens: [
                            ...currentReview.screens.map(aiReviewScreen => ({
                                ...aiReviewScreen,
                                reviewStatus: AIReviewScreenStatus.CANCELLED
                            }))
                        ]
                    }
                }
            }
        };
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            case AppActionTypes.RESET:
            case BarrelActionTypes.RESET:
                return this.reset(state);

            case ApprovalsActionTypes.GET_PROJECT_APPROVALS_SUCCESS:
                return this.getApprovalsSuccess(state, action);
            case ApprovalsActionTypes.GET_PROJECT_APPROVALS_FINISH:
                return this.getApprovalsFinish(state);

            case ApprovalsActionTypes.GET_WORKSPACE_APPROVALS_SUCCESS:
                return this.getWorkspaceApprovalsSuccess(state, action);

            case ApprovalsActionTypes.CREATE_APPROVAL:
                return this.createApproval(state);
            case ApprovalsActionTypes.CREATE_APPROVAL_SUCCESS:
                return this.createApprovalSuccess(state, action);
            case ApprovalsActionTypes.CREATE_APPROVAL_FAILURE:
                return this.createApprovalFailure(state);
            case ApprovalsActionTypes.ADD_APPROVAL:
                return this.addApproval(state, action);

            case ApprovalsActionTypes.UPDATE_APPROVAL:
                return this.updateApproval(state);
            case ApprovalsActionTypes.UPDATE_APPROVAL_SUCCESS:
                return this.updateApprovalSuccess(state, action);
            case ApprovalsActionTypes.UPDATE_APPROVAL_FAILURE:
                return this.updateApprovalFailure(state);

            case ApprovalsActionTypes.UPDATE_APPROVAL_REVIEW:
                return this.updateApprovalReview(state);
            case ApprovalsActionTypes.UPDATE_APPROVAL_REVIEW_SUCCESS:
                return this.updateApprovalReviewSuccess(state, action);
            case ApprovalsActionTypes.UPDATE_APPROVAL_REVIEW_FAILURE:
                return this.updateApprovalReviewFailure(state);

            case ApprovalsActionTypes.RE_REQUEST_APPROVAL_REVIEW:
                return this.reRequestApprovalReview(state);
            case ApprovalsActionTypes.RE_REQUEST_APPROVAL_REVIEW_SUCCESS:
                return this.reRequestApprovalReviewSuccess(state, action);
            case ApprovalsActionTypes.RE_REQUEST_APPROVAL_REVIEW_FAILURE:
                return this.reRequestApprovalReviewFailure(state);

            case ApprovalsActionTypes.DELETE_APPROVAL_REVIEWER:
                return this.deleteApprovalReviewer(state);
            case ApprovalsActionTypes.DELETE_APPROVAL_REVIEWER_SUCCESS:
                return this.deleteApprovalReviewerSuccess(state, action);
            case ApprovalsActionTypes.DELETE_APPROVAL_REVIEWER_FAILURE:
                return this.deleteApprovalReviewerFailure(state);

            case ApprovalsActionTypes.DELETE_APPROVAL:
                return this.deleteApproval(state, action);

            case ApprovalsActionTypes.ADD_WORKSPACE_APPROVAL:
                return this.addWorkspaceApproval(state, action);
            case ApprovalsActionTypes.UPDATE_WORKSPACE_APPROVAL:
                return this.updateWorkspaceApproval(state, action);
            case ApprovalsActionTypes.UPDATE_WORKSPACE_APPROVAL_REVIEW:
                return this.updateWorkspaceApprovalReview(state, action);
            case ApprovalsActionTypes.DELETE_WORKSPACE_APPROVAL_REVIEWER:
                return this.deleteWorkspaceApprovalReviewer(state, action);
            case ApprovalsActionTypes.DELETE_WORKSPACE_APPROVAL:
                return this.deleteWorkspaceApproval(state, action);

            case ApprovalsActionTypes.CREATE_AI_REVIEW:
                return this.createAIReview(state);
            case ApprovalsActionTypes.CREATE_AI_REVIEW_SUCCESS:
                return this.createAIReviewSuccess(state, action);
            case ApprovalsActionTypes.CREATE_AI_REVIEW_FAILURE:
                return this.createAIReviewFailure(state);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW:
                return this.updateAIReview(state, action);
            case ApprovalsActionTypes.CREATE_AI_REVIEW_FEEDBACK:
                return this.createAIReviewFeedback(state, action);
            case ApprovalsActionTypes.DELETE_AI_REVIEW_FEEDBACK:
                return this.deleteAIReviewFeedback(state, action);
            case ApprovalsActionTypes.CREATE_AI_REVIEW_SCREEN_DOT_RATING:
                return this.createAiReviewScreenDotRating(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_STATUS:
                return this.updateAIReviewStatus(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_STATUS:
                return this.updateAIReviewScreenStatus(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_CATEGORY_STATUS:
                return this.updateAIReviewScreenCategoryStatus(state, action);
            case ApprovalsActionTypes.CREATE_AI_REVIEW_SCREEN_DOT:
                return this.createAIReviewScreenDot(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_DOT:
                return this.updateAIReviewScreenDot(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_DOT_STATUS:
                return this.updateAIReviewScreenDotStatus(state, action);
            case ApprovalsActionTypes.DELETE_AI_REVIEW_SCREEN_DOT:
                return this.deleteAIReviewScreenDot(state, action);
            case ApprovalsActionTypes.DELETE_AI_REVIEW:
                return this.deleteAIReview(state, action);
            case ApprovalsActionTypes.STOP_AI_REVIEW:
                return this.stopAIReview(state, action);

            default:
                return state;
        }
    }
}

export default ApprovalsDataStore;
export {
    State as ApprovalsDataStoreState
};
